/** Driver app (DA) UI strings — English + Hindi */

import {
  canonicalDeliverySlot,
  deliverySlotWindowLabel,
  isCustomDeliverySlot,
} from "@/lib/deliverySlotLabels";

export const DRIVER_LANG_STORAGE_KEY = "ttt_driver_lang";

export const DRIVER_STRINGS = {
  en: {
    help: "Help",
    langSwitchToHi: "हिंदी",
    langSwitchToEn: "English",
    langSwitchAriaToHi: "Switch to Hindi",
    langSwitchAriaToEn: "Switch to English",

    loading: "Loading…",
    invalidLink: "This page needs a valid driver link. Ask your manager for the link.",
    requestFailed: "Request failed",

    // Role picker
    titleDriver: "Driver",
    roleIntro: "Pick what you're doing — use the arrows on each screen to move between days.",
    delivery: "Delivery",
    deliverySub: "Runs, handoff & COD",
    purchase: "Purchase",
    purchaseSub: "Log buys & receipts",
    customDeliveryAdmin: "Custom runs",
    customDeliveryAdminSub: "Add delivery or pickup for drivers",
    customDeliveryAdminTitle: "Custom runs",
    customDeliveryAdminHint:
      "Create a delivery or pickup run — price is distance × partner rate. It goes on the shared board for all drivers.",
    customRunTypeLabel: "Run type",
    customRunTypeDelivery: "Delivery",
    customRunTypePickup: "Pickup",
    customDeliveryAdd: "New custom run",
    customDeliveryAddDelivery: "New custom delivery",
    customDeliveryAddPickup: "New custom pickup",
    customDeliveryCustomer: "Customer name",
    customDeliveryPhone: "Phone (optional)",
    customDeliveryItem: "Cake flavour",
    customDeliveryItemPlaceholder: "Select cake flavour…",
    customDeliveryPickFlavor: "Pick which cake flavour is being delivered.",
    customDeliveryPickFlavorPickup: "Pick which cake flavour is being picked up.",
    customDeliveryLocation: "Delivery address (Google)",
    customDeliveryLocationPickup: "Pickup address (Google)",
    customDeliveryLocationPlaceholder: "Type and pick a full Google address…",
    customDeliveryPickGoogle: "Pick an address from the Google list to calculate price.",
    customDeliveryPickGooglePickup: "Pick a pickup address from the Google list to calculate price.",
    customDeliveryGoogleLoading: "Searching Google Places…",
    customDeliveryGoogleEmpty: "No Google matches — keep typing.",
    customDeliveryGoogleError:
      "Google address search didn’t load. Check network or try again.",
    customDeliveryBlock: "Block / flat (optional)",
    customDeliveryArea: "Area (optional)",
    customDeliverySlot: "Time slot",
    customDeliverySlotDelivery: "Delivery slot",
    customDeliverySlotPickup: "Pickup slot",
    customDeliveryNotes: "Notes (optional)",
    customDeliveryQuoteLoading: "Calculating price…",
    customDeliveryQuote: "{km} km × ₹{rate}/km → collect {price}",
    customDeliverySendOut: "Send out",
    customDeliveryAlreadyPaid: "Already paid — no collection at door",
    customDeliveryCollectAmount: "Amount to collect (₹)",
    customDeliveryCollectAmountPlaceholder: "e.g. 250",
    customDeliveryCollectHint: "Driver will see this amount, UPI QR, and Cash/UPI buttons.",
    customDeliveryCollectHintPickup: "Driver will collect this amount at pickup — UPI QR and Cash/UPI buttons.",
    customDeliveryCollectAmountRequired: "Enter the amount the driver should collect.",
    customDeliveryPaidHint: "Driver will only take a door photo and mark delivered.",
    customDeliveryCreatedToday: "Sent out this session",
    customDeliveryListTitle: "Custom runs this day",
    customDeliveryEditTitle: "Edit custom run",
    customDeliveryEditTitleDelivery: "Edit custom delivery",
    customDeliveryEditTitlePickup: "Edit custom pickup",
    customDeliveryEdit: "Edit",
    customDeliveryDelete: "Delete",
    customDeliveryDeleteConfirm: "Delete {label}? This removes it from the driver board.",
    customDeliverySaveChanges: "Save changes",
    customDeliveryCompleted: "Completed",
    deliveredBy: "Delivered by {name}",
    takenBy: "Taken by {name}",
    customDeliveryLocked: "Locked",
    customDeliveryEmpty: "No custom runs added yet today.",
    customDeliveryBadge: "Custom",
    customPickupBadge: "Pickup",

    // Dashboard list
    titleDeliveries: "Deliveries",
    back: "Back",
    offlineList: "No signal — list will refresh when you're back online.",
    deliveryRuns: "Delivery runs",
    refresh: "Refresh",
    prevDay: "Previous day",
    nextDay: "Next day",
    today: "Today",
    tabDeliveries: "Deliveries",
    tabCompleted: "Completed",
    loadingDeliveries: "Loading deliveries…",
    emptyCompleted: "No completed deliveries for this day.",
    sectionStillUnpaid: "Still unpaid — payment due",
    sectionCompletedFirst: "Completed — #1 first",
    sectionCompletedDayFirst: "Completed this day — #1 first",
    noOtherCompletions: "No other completions for {date}",
    emptyScheduled: "No delivery cakes scheduled for this day.",
    sectionOutForDelivery: "Out for delivery — pick your runs below",
    sectionOutUnclaimed: "Tap orders you are taking — match the # on the cake box",
    claimDeliveriesDone: "Done — I'm taking these",
    claimDeliveriesHint: "Read the order # on each cake box, select your deliveries, then tap Done.",
    claimDeliveriesWorking: "Claiming…",
    claimDeliveriesNone: "Could not claim — another driver may have taken them.",
    claimDeliveriesPartial: "Claimed {n} — some were already taken",
    claimDeliveriesSuccess: "Claimed {n} — customer notified",
    claimTaken: "Taken",
    claimPickMe: "Pick",
    settlementPendingBanner: "Pending with you: ₹{amount} — show manager before next delivery",
    settlementBlockCash: "Hand ₹{amount} cash to manager for order #{order} before next delivery",
    settlementBlockUpi: "Get UPI confirmed by manager for order #{order} (₹{amount}) before next delivery",
    settlementClaimDisabled: "Settle your last Cash/UPI payment with manager before claiming more deliveries",
    claimBlockedTapHint: "Hand ₹{amount} to manager first (order #{order}) — tap Handover at top",
    handoverBtn: "Handover",
    handoverBtnSub: "Cash & UPI you owe the manager",
    handoverBtnBadge: "₹{amount} due",
    handoverTitle: "Handover",
    handoverBack: "Back",
    handoverLoading: "Loading handover…",
    handoverDueLabel: "Hand to manager now",
    handoverDueSub: "Give this cash or show UPI proof to your manager immediately.",
    handoverDueBreakdown: "Cash ₹{cash} · UPI ₹{upi}",
    handoverAllClear: "Nothing due",
    handoverAllClearSub: "Manager confirmed — you're clear for more deliveries.",
    handoverPendingListTitle: "Pending with you",
    handoverOrderLine: "Order #{order}",
    handoverConfirmedTodayTitle: "Confirmed today (manager approved)",
    handoverConfirmedTotal: "Total today ₹{amount}",
    handoverRefresh: "Refresh",
    handoverRefreshing: "Refreshing…",
    upiProofPrompt: "Upload UPI payment screenshot now? (Helps manager confirm faster)",
    sectionScheduled: "Scheduled — waiting for manager",
    searchOrdersPlaceholder: "Search order # or address…",
    searchOrdersAria: "Search deliveries by order number or address",
    searchNoMatches: "No match — showing full list",
    dryCakeHint: "Dry cake orders marked ready can be sent Out from the manager app.",
    outNow: "Out now",
    scheduled: "Scheduled",
    priority: "Priority",
    dryCake: "Dry Cake",
    cake: "Cake",
    deliveryOrderN: "Delivery order {n}",
    payPaid: "PAID",
    payUnpaid: "UNPAID",
    payCod: "COD",

    // Purchase list
    titlePurchase: "Purchase",
    outOfPocketBuys: "Out-of-pocket buys",
    loadingPurchases: "Loading purchases…",
    dayTotalReimburse: "Day total to reimburse",
    item: "item",
    items: "items",
    emptyPurchases: "No purchases logged for this day yet.",
    emptyPurchasesHint: "Tap below to add one.",
    addPurchase: "Add purchase",
    logPurchase: "Log a purchase",
    close: "Close",
    whoPaid: "Who paid?",
    whatBought: "What did you buy?",
    optional: "(optional)",
    whatBoughtPlaceholder: "e.g. Eggs 2 trays, cream, boxes…",
    priceInr: "Price (₹)",
    photo: "Photo",
    tapPhoto: "Tap to take or choose photo",
    changePhoto: "Change photo",
    saving: "Saving…",
    savePurchase: "Save purchase",
    selectWhoPaid: "Select who paid.",
    enterValidPrice: "Enter a valid price.",
    addPhotoRequired: "Add a photo of the item or receipt.",
    deletePurchaseConfirm: 'Delete "{label}"?',
    thisPurchase: "this purchase",
    viewFullPhoto: "View full photo",
    deletePurchase: "Delete purchase",
    fullSizePhoto: "Full size photo",
    purchaseStillOwed: "Still owed",
    purchaseAllPaid: "All reimbursed",
    purchaseMarkPaid: "Mark paid",
    purchaseMarkUnpaid: "Mark unpaid",
    purchasePaidBack: "Paid back",
    purchaseStillUnpaid: "Still owed",
    inventoryApplyTitle: "Apply to M Inventory",
    inventoryApplySub: "Select what was bought and save it into Inventory.",
    inventoryAddEntry: "Add entry",
    inventoryApplyTo: "Apply to",
    inventoryTypeRaw: "Raw material",
    inventoryTypeDecor: "Decor stock",
    inventoryTypeDaily: "Daily expense",
    inventoryTypeCapex: "CapEx",
    inventoryItem: "Item",
    inventoryAccount: "Account",
    inventoryQty: "Qty",
    inventoryTotalPaid: "Total paid (₹)",
    inventorySupplier: "Supplier",
    inventorySupplierPlaceholder: "e.g. vendor / shop",
    inventoryNotes: "Notes",
    inventoryNotesPlaceholder: "e.g. bill no, reason",
    inventorySearchPlaceholder: "Search item name…",
    inventoryNoMatches: "No matching item found.",
    inventoryCreateNew: "Create new",
    inventoryNewItemName: "New item name",
    inventoryNewAccountName: "New account name",
    inventoryDecorCategory: "Decor category",
    inventoryUnit: "Unit",
    inventoryNewNameRequired: "Enter the new item/account name.",
    inventorySaveToM: "Save to M Inventory",
    inventoryAppliedToday: "Applied today in M",
    inventorySelectItem: "Select an item or account.",
    inventoryEnterQty: "Enter a valid quantity.",

    // Handoff detail
    titleDelivery: "Delivery",
    allDeliveries: "All deliveries",
    offlineHandoff: "No signal — order saved on phone; uploads will sync later.",
    sharingLocation: "Sharing live location with customer on their cake link",
    syncing: "Syncing…",
    tapToSync: "Tap to sync",
    syncNow: "Sync now",
    waitingInternet: "Waiting for internet to finish this delivery.",
    deliverySavedOffline: "Delivery saved on your phone — will sync when signal returns.",
    loadingOrder: "Loading order…",
    order: "Order",
    notesForDriver: "Notes for driver",
    deliver: "Deliver",
    dropOff: "Drop-off",
    openGoogleMaps: "Open in Google Maps",
    payment: "Payment",
    paid: "PAID",
    paymentReceived: "✓ Payment already received",
    unpaid: "UNPAID",
    payLaterHint: "Customer will pay later — complete after door photo",
    upiReceived: "UPI received",
    upiMarkedPending: "UPI marked — waiting for bank",
    cashReceived: "Cash received",
    changePayment: "Change payment method",
    cancelChangePayment: "Cancel",
    paymentSyncing: "Saving payment… wait a moment, then slide.",
    finishPaymentChange: "Pick Cash, UPI, or Unpaid again before sliding.",
    totalMinusAdvance: "Total ₹{total} − advance ₹{advance}",
    cash: "Cash",
    upi: "UPI",
    unpaidPayLater: "Unpaid — pay later",
    upiCustomerScans: "UPI — customer scans to pay",
    upiQrMissing: "Set business UPI ID in Team settings to enable amount-specific QR.",
    showFixedQr: "Show fixed QR",
    showQrAgain: "Show QR",
    hideQrAgain: "Hide QR",
    qrFallbackHint: "Customer can scan again if payment did not go through",
    upiQrAlt: "The Table Top UPI QR",
    scanToPayOrder: "Scan to pay this order",
    customerVerification: "Customer verification required",
    highValueCod: "High-value COD — verify customer identity",
    codeVerified: "✓ Code verified",
    sendCodeToCustomer: "Send code to customer",
    otpPlaceholder: "6-digit code",
    verify: "Verify",
    savedOnPhone: "✓ Saved on phone (will upload)",
    doorPhotoReady: "✓ Door photo ready",
    retake: "Retake",
    camera: "Camera",
    gallery: "Gallery",
    cakeAtDoor: "Cake at door",
    slideToDeliver: "Slide to mark delivered",
    orderComplete: "ORDER COMPLETE",
    callCustomer: "Call customer",

    // Cake proof
    cakeProof: "Cake proof",
    finishedCake: "Finished cake",
    onScale: "On scale",
    tapToEnlarge: "· tap to enlarge",
    orderedWeight: "Ordered weight:",
    openProofForCustomer: "Open proof page for customer",

    // Live map
    toDropOff: "To drop-off",
    min: "min",
    minutesToDropOff: "{n} minutes to drop-off",
    syncingLiveRoute: "Syncing live route",
    yourRoute: "Your route",
    liveEtaRoads: "Live ETA on roads ahead",
    waitingGpsSync: "Waiting for GPS sync…",
    tapDirections: "Tap below to open turn-by-turn directions",
    liveRouteCustomer: "Live route to customer",
    closeFullscreenMap: "Close full screen map",
    routeMap: "Route map",
    exitFullscreenMap: "Exit full screen map",
    openFullscreenMap: "Open full screen map",
    openRouteGoogleMaps: "Open route in Google Maps",

    // Thank you
    thankYou: "Thank you, {name} ji!",
    orderNowComplete: "The order is now complete",
    backToAllDeliveries: "Back to all deliveries",
    there: "there",

    // Confirm / alerts
    codPaidConfirm:
      "Mark this order as paid?\n\nOnly tap OK if the customer has already paid ₹{amount} by {mode}. This updates the order — it does not pay the delivery partner.",
    payLaterConfirm:
      "Customer will pay later?\n\nThe order can be completed without payment proof. Customer will get a WhatsApp with UPI payment details when you slide to complete.",
    otpSentAlert: "Code sent to customer on WhatsApp. Ask them to read it to you.",

    // Errors / validation
    noInternetCache:
      "No internet — open this link once with good signal, then try again.",
    weakSignalCache: "Weak signal — showing saved order. Pull down to refresh when signal improves.",
    photoSavedPhone:
      "Upload service failed. Photo is saved on this phone and will retry automatically.",
    orderNotLoaded: "Order not loaded.",
    tapPaymentFirst:
      "Tap Cash, UPI, or Unpaid (pay later) after talking to the customer.",
    paymentNotConfirmed: "Payment not confirmed yet.",
    takeDoorPhoto: "Take a photo of the cake at the door before completing.",
    verifyOtpFirst: "Verify customer OTP before completing.",
    deliverySavedPhone: "Delivery saved on your phone — will finish when internet is back.",
    couldNotUploadPhotos:
      "Upload service could not accept the saved photos. They remain on this phone; wait a moment and slide once.",
    deliverySavedAuto: "Delivery saved — will sync automatically.",
    codPaidFailed: "Could not save payment. Refreshed order — check status and try again.",

    couldNotOpenPage: "Could not open driver page.",

    // Map buffer messages (from liveTrackingMap)
    mapPinningDropOff: "Pinning drop-off on map…",
    mapAllowLocation: "Allow location — ETA when GPS is on",
    mapBufferingLocation: "Buffering — syncing your location…",
    mapBufferingGps: "Buffering — refreshing GPS…",
    mapBufferingRoute: "Buffering — matching route to customer…",

    brandSubtitle: "The Table Top",
    docTitle: "Delivery — The Table Top",
    priorityTitle: "⚡ PRIORITY ORDER",
    priorityHint: "Customer needs this immediately — finish before other orders",
    photoLabel: "Photo",
    kgUnit: "kg",

    slotMorning: "Morning",
    slotNoon: "Noon",
    slotEvening: "Evening",
    slotNight: "Night",
    slotCustom: "Custom",

    errInvalidDriverLink: "Invalid driver link",
    errSelectOrder: "Select an order from the delivery list",
    errNotOutYet: "This order is not out for delivery yet",
    errCouldNotVerify: "Could not verify order",
    errCouldNotLoadOrder: "Could not load this order. Please try again or ask your manager.",
    errCouldNotLoadOrderRefresh: "Could not load this order. Please ask your manager to refresh the driver link.",
    errMarkPaymentFirstServer: "Mark Cash, UPI, or Unpaid (pay later) on the driver link before completing.",
    errOtpRequiredServer:
      "High-value COD delivery requires customer OTP verification. Use the driver link to send and verify the code.",
    errOrderNotFound: "Order not found",
    errDbFailed: "Database connection failed",
    errInvalidHandoff: "Invalid order for driver handoff",
    errProofUploadFailed: "Proof upload failed",
    errDeliveryPhotoUploadFailed: "Delivery photo upload failed",
    errPhotosStillUploading: "Saved photos still uploading. Try again in a moment.",
    errUploadFailed: "Upload failed",

    installTitle: "Delivery app",
    installSubtitle: "Driver dashboard",
    installContinue: "Open Delivery",
    installAddHomeTitle: "Add to home screen (separate from the other app):",
    installStep1: "1. Stay on this page",
    installStep2: "2. Chrome menu (⋮) → Install app or Add to Home screen",
    installStep3Name: "3. Name it Delivery",
    installAppName: "Delivery",
  },
  hi: {
    help: "मदद",
    langSwitchToHi: "हिंदी",
    langSwitchToEn: "English",
    langSwitchAriaToHi: "हिंदी में बदलें",
    langSwitchAriaToEn: "अंग्रेज़ी में बदलें",

    loading: "लोड हो रहा है…",
    invalidLink: "इस पेज के लिए सही ड्राइवर लिंक चाहिए। मैनेजर से लिंक माँगें।",
    requestFailed: "अनुरोध विफल",

    titleDriver: "ड्राइवर",
    roleIntro: "आप क्या कर रहे हैं चुनें — हर स्क्रीन पर तीर से दिन बदल सकते हैं।",
    delivery: "डिलीवरी",
    deliverySub: "रन, हैंडऑफ़ और COD",
    purchase: "खरीद",
    purchaseSub: "खरीद और रसीद दर्ज करें",
    customDeliveryAdmin: "कस्टम रन",
    customDeliveryAdminSub: "डिलीवरी या पिकअप जोड़ें",
    customDeliveryAdminTitle: "कस्टम रन",
    customDeliveryAdminHint:
      "डिलीवरी या पिकअप रन बनाएं — कीमत = दूरी × पार्टनर रेट। सभी ड्राइवरों की सूची में दिखेगा।",
    customRunTypeLabel: "रन प्रकार",
    customRunTypeDelivery: "डिलीवरी",
    customRunTypePickup: "पिकअप",
    customDeliveryAdd: "नया कस्टम रन",
    customDeliveryAddDelivery: "नई कस्टम डिलीवरी",
    customDeliveryAddPickup: "नया कस्टम पिकअप",
    customDeliveryCustomer: "ग्राहक का नाम",
    customDeliveryPhone: "फ़ोन (वैकल्पिक)",
    customDeliveryItem: "केक का स्वाद",
    customDeliveryItemPlaceholder: "क्लासिक केक का स्वाद चुनें",
    customDeliveryPickFlavor: "कौन सा केक स्वाद भेजना है — चुनें।",
    customDeliveryPickFlavorPickup: "कौन सा केक स्वाद उठाना है — चुनें।",
    customDeliveryLocation: "डिलीवरी पता (Google)",
    customDeliveryLocationPickup: "पिकअप पता (Google)",
    customDeliveryLocationPlaceholder: "टाइप करें और Google पता चुनें…",
    customDeliveryPickGoogle: "कीमत के लिए Google सूची से पता चुनें।",
    customDeliveryPickGooglePickup: "कीमत के लिए Google से पिकअप पता चुनें।",
    customDeliveryGoogleLoading: "Google Places खोज…",
    customDeliveryGoogleEmpty: "कोई Google मैच नहीं — टाइप करते रहें।",
    customDeliveryGoogleError:
      "Google पता खोज लोड नहीं हुई। नेटवर्क जांचें।",
    customDeliveryBlock: "ब्लॉक / फ्लैट (वैकल्पिक)",
    customDeliveryArea: "एरिया (वैकल्पिक)",
    customDeliverySlot: "समय स्लॉट",
    customDeliverySlotDelivery: "डिलीवरी स्लॉट",
    customDeliverySlotPickup: "पिकअप स्लॉट",
    customDeliveryNotes: "नोट (वैकल्पिक)",
    customDeliveryQuoteLoading: "कीमत गिन रहे हैं…",
    customDeliveryQuote: "{km} km × ₹{rate}/km → वसूली {price}",
    customDeliverySendOut: "भेजें",
    customDeliveryAlreadyPaid: "पहले से भुगतान — दरवाज़े पर वसूली नहीं",
    customDeliveryCollectAmount: "वसूली राशि (₹)",
    customDeliveryCollectAmountPlaceholder: "जैसे 250",
    customDeliveryCollectHint: "ड्राइवर को यह राशि, UPI QR और Cash/UPI बटन दिखेंगे।",
    customDeliveryCollectHintPickup: "ड्राइवर पिकअप पर यह राशि वसूलेगा — UPI QR और Cash/UPI बटन।",
    customDeliveryCollectAmountRequired: "ड्राइवर कितना वसूले — राशि लिखें।",
    customDeliveryPaidHint: "ड्राइवर सिर्फ फोटो लेकर डिलीवर करेगा।",
    customDeliveryCreatedToday: "इस सेशन में भेजे गए",
    customDeliveryListTitle: "इस दिन के कस्टम रन",
    customDeliveryEdit: "संपादित",
    customDeliveryDelete: "हटाएँ",
    customDeliveryDeleteConfirm: "{label} हटाएँ? यह ड्राइवर बोर्ड से हट जाएगी।",
    customDeliverySaveChanges: "बदलाव सहेजें",
    customDeliveryEditTitle: "कस्टम रन संपादित",
    customDeliveryEditTitleDelivery: "कस्टम डिलीवरी संपादित",
    customDeliveryEditTitlePickup: "कस्टम पिकअप संपादित",
    customDeliveryCompleted: "पूर्ण",
    deliveredBy: "{name} ने डिलीवर किया",
    takenBy: "{name} ने लिया",
    customDeliveryLocked: "लॉक",
    customDeliveryEmpty: "आज अभी कोई कस्टम रन नहीं।",
    customDeliveryBadge: "कस्टम",
    customPickupBadge: "पिकअप",
    back: "वापस",
    offlineList: "नेटवर्क नहीं — ऑनलाइन आने पर सूची अपडेट होगी।",
    deliveryRuns: "डिलीवरी रन",
    refresh: "रीफ़्रेश",
    prevDay: "पिछला दिन",
    nextDay: "अगला दिन",
    today: "आज",
    tabDeliveries: "डिलीवरी",
    tabCompleted: "पूर्ण",
    loadingDeliveries: "डिलीवरी लोड हो रही है…",
    emptyCompleted: "इस दिन कोई पूर्ण डिलीवरी नहीं।",
    sectionStillUnpaid: "अभी भी बकाया — भुगतान बाकी",
    sectionCompletedFirst: "पूर्ण — #1 पहले",
    sectionCompletedDayFirst: "इस दिन पूर्ण — #1 पहले",
    noOtherCompletions: "{date} के लिए और कोई पूर्ण नहीं",
    emptyScheduled: "इस दिन कोई केक डिलीवरी निर्धारित नहीं।",
    sectionOutForDelivery: "डिलीवरी पर — नीचे अपनी रन चुनें",
    sectionOutUnclaimed: "जो ले जा रहे हैं उन्हें टैप करें — केक बॉक्स # से मिलाएँ",
    claimDeliveriesDone: "Done — मैं ये ले जा रहा/रही हूँ",
    claimDeliveriesHint: "केक बॉक्स पर ऑर्डर # पढ़ें, चुनें, फिर Done दबाएँ।",
    claimDeliveriesWorking: "क्लेम हो रहा है…",
    claimDeliveriesNone: "क्लेम नहीं हुआ — शायद कोई और ले गया",
    claimDeliveriesPartial: "{n} क्लेम — कुछ पहले ही लिए गए",
    claimDeliveriesSuccess: "{n} क्लेम — ग्राहक को सूचना भेजी",
    claimTaken: "ले लिया",
    claimPickMe: "चुनें",
    settlementPendingBanner: "आपके पास लंबित: ₹{amount} — अगली डिलीवरी से पहले मैनेजर को दिखाएँ",
    settlementBlockCash: "ऑर्डर #{order} का ₹{amount} नकद मैनेजर को दें — फिर अगली डिलीवरी",
    settlementBlockUpi: "ऑर्डर #{order} का UPI (₹{amount}) मैनेजर से कन्फर्म कराएँ — फिर अगली डिलीवरी",
    settlementClaimDisabled: "पहले पिछला Cash/UPI मैनेजर से सettle करें, फिर नई डिलीवरी चुनें",
    claimBlockedTapHint: "पहले ₹{amount} मैनेजर को दें (ऑर्डर #{order}) — ऊपर Handover टैप करें",
    handoverBtn: "Handover",
    handoverBtnSub: "मैनेजर को देने वाला Cash/UPI",
    handoverBtnBadge: "₹{amount} बकाया",
    handoverTitle: "Handover",
    handoverBack: "वापस",
    handoverLoading: "Handover लोड हो रहा है…",
    handoverDueLabel: "अभी मैनेजर को दें",
    handoverDueSub: "अगली डिलीवरी से पहले यह नकद दें या UPI प्रूफ दिखाएँ।",
    handoverDueBreakdown: "नकद ₹{cash} · UPI ₹{upi}",
    handoverAllClear: "कुछ बकाया नहीं",
    handoverAllClearSub: "मैनेजर ने कन्फर्म किया — अब और डिलीवरी ले सकते हैं।",
    handoverPendingListTitle: "आपके पास लंबित",
    handoverOrderLine: "ऑर्डर #{order}",
    handoverConfirmedTodayTitle: "आज कन्फर्म (मैनेजर ने मंजूर)",
    handoverConfirmedTotal: "आज कुल ₹{amount}",
    handoverRefresh: "रीफ़्रेश",
    handoverRefreshing: "रीफ़्रेश हो रहा है…",
    upiProofPrompt: "UPI screenshot अभी अपलोड करें? (मैनेजर को जल्दी कन्फर्म होगा)",
    sectionScheduled: "निर्धारित — मैनेजर का इंतज़ार",
    searchOrdersPlaceholder: "ऑर्डर # या पता खोजें…",
    searchOrdersAria: "ऑर्डर नंबर या पते से खोजें",
    searchNoMatches: "कोई मिलान नहीं — पूरी सूची",
    dryCakeHint: "तैयार ड्राई केक ऑर्डर मैनेजर ऐप से Out भेजे जा सकते हैं।",
    outNow: "अभी बाहर",
    scheduled: "निर्धारित",
    priority: "प्राथमिकता",
    dryCake: "ड्राई केक",
    cake: "केक",
    deliveryOrderN: "डिलीवरी क्रम {n}",
    payPaid: "भुगतान",
    payUnpaid: "बकाया",
    payCod: "COD",

    titlePurchase: "खरीद",
    outOfPocketBuys: "जेब से खरीद",
    loadingPurchases: "खरीद लोड हो रही है…",
    dayTotalReimburse: "दिन का कुल वापसी",
    item: "आइटम",
    items: "आइटम",
    emptyPurchases: "इस दिन अभी कोई खरीद दर्ज नहीं।",
    emptyPurchasesHint: "नीचे टैप करके जोड़ें।",
    addPurchase: "खरीद जोड़ें",
    logPurchase: "खरीद दर्ज करें",
    close: "बंद",
    whoPaid: "किसने भुगतान किया?",
    whatBought: "क्या खरीदा?",
    optional: "(वैकल्पिक)",
    whatBoughtPlaceholder: "जैसे अंडे 2 ट्रे, क्रीम, बॉक्स…",
    priceInr: "कीमत (₹)",
    photo: "फ़ोटो",
    tapPhoto: "फ़ोटो लेने या चुनने के लिए टैप करें",
    changePhoto: "फ़ोटो बदलें",
    saving: "सेव हो रहा है…",
    savePurchase: "खरीद सेव करें",
    selectWhoPaid: "किसने भुगतान किया चुनें।",
    enterValidPrice: "सही कीमत दर्ज करें।",
    addPhotoRequired: "आइटम या रसीद की फ़ोटो जोड़ें।",
    deletePurchaseConfirm: '"{label}" हटाएँ?',
    thisPurchase: "यह खरीद",
    viewFullPhoto: "पूरी फ़ोटो देखें",
    deletePurchase: "खरीद हटाएँ",
    fullSizePhoto: "पूर्ण आकार फ़ोटो",
    purchaseStillOwed: "अभी बकाया",
    purchaseAllPaid: "सब वापस मिल गया",
    purchaseMarkPaid: "भुगतान हो गया",
    purchaseMarkUnpaid: "अभी बकाया",
    purchasePaidBack: "वापस मिल गया",
    purchaseStillUnpaid: "अभी बकाया",
    inventoryApplyTitle: "M Inventory में जोड़ें",
    inventoryApplySub: "जो खरीदा है उसे चुनकर Inventory में सेव करें।",
    inventoryAddEntry: "एंट्री जोड़ें",
    inventoryApplyTo: "कहाँ जोड़ना है",
    inventoryTypeRaw: "Raw material",
    inventoryTypeDecor: "Decor stock",
    inventoryTypeDaily: "Daily expense",
    inventoryTypeCapex: "CapEx",
    inventoryItem: "आइटम",
    inventoryAccount: "अकाउंट",
    inventoryQty: "मात्रा",
    inventoryTotalPaid: "कुल भुगतान (₹)",
    inventorySupplier: "Supplier",
    inventorySupplierPlaceholder: "जैसे vendor / shop",
    inventoryNotes: "Notes",
    inventoryNotesPlaceholder: "जैसे bill no, reason",
    inventorySearchPlaceholder: "आइटम नाम खोजें…",
    inventoryNoMatches: "कोई matching item नहीं मिला।",
    inventoryCreateNew: "नया बनाएं",
    inventoryNewItemName: "नए आइटम का नाम",
    inventoryNewAccountName: "नए अकाउंट का नाम",
    inventoryDecorCategory: "Decor category",
    inventoryUnit: "Unit",
    inventoryNewNameRequired: "नए item/account का नाम डालें।",
    inventorySaveToM: "M Inventory में सेव करें",
    inventoryAppliedToday: "आज M में जुड़ा",
    inventorySelectItem: "आइटम या अकाउंट चुनें।",
    inventoryEnterQty: "सही मात्रा दर्ज करें।",

    titleDelivery: "डिलीवरी",
    allDeliveries: "सभी डिलीवरी",
    offlineHandoff: "नेटवर्क नहीं — ऑर्डर फ़ोन में सेव; बाद में अपलोड होगा।",
    sharingLocation: "ग्राहक को उनके केक लिंक पर लाइव लोकेशन दिख रही है",
    syncing: "सिंक हो रहा है…",
    tapToSync: "सिंक के लिए टैप",
    syncNow: "अभी सिंक",
    waitingInternet: "डिलीवरी पूरी करने के लिए इंटरनेट का इंतज़ार।",
    deliverySavedOffline: "डिलीवरी फ़ोन में सेव — सिग्नल आने पर सिंक होगी।",
    loadingOrder: "ऑर्डर लोड हो रहा है…",
    order: "ऑर्डर",
    notesForDriver: "ड्राइवर के लिए नोट",
    deliver: "डिलीवर करें",
    dropOff: "ड्रॉप-ऑफ़",
    openGoogleMaps: "Google Maps में खोलें",
    payment: "भुगतान",
    paid: "भुगतान",
    paymentReceived: "✓ भुगतान पहले ही मिल चुका",
    unpaid: "बकाया",
    payLaterHint: "ग्राहक बाद में भुगतान करेगा — दरवाज़े की फ़ोटो के बाद पूर्ण करें",
    upiReceived: "UPI मिल गया",
    upiMarkedPending: "UPI दर्ज — बैंक की पुष्टि बाकी",
    cashReceived: "नकद मिल गया",
    changePayment: "पेमेंट तरीका बदलें",
    cancelChangePayment: "रद्द करें",
    paymentSyncing: "पेमेंट सेव हो रहा है… थोड़ा रुककर स्लाइड करें।",
    finishPaymentChange: "स्लाइड से पहले फिर से नकद, UPI, या बकाया चुनें।",
    totalMinusAdvance: "कुल ₹{total} − अग्रिम ₹{advance}",
    cash: "नकद",
    upi: "UPI",
    unpaidPayLater: "बकाया — बाद में भुगतान",
    upiCustomerScans: "UPI — ग्राहक स्कैन करके भुगतान",
    upiQrMissing: "राशि वाला QR के लिए Team सेटिंग में UPI ID सेट करें।",
    showFixedQr: "फ़िक्स्ड QR दिखाएँ",
    showQrAgain: "QR दिखाएँ",
    hideQrAgain: "QR छुपाएँ",
    qrFallbackHint: "अगर पेमेंट नहीं हुआ तो कस्टमर फिर स्कैन कर सकता है",
    upiQrAlt: "The Table Top UPI QR",
    scanToPayOrder: "भुगतान के लिए स्कैन करें",
    customerVerification: "ग्राहक सत्यापन ज़रूरी",
    highValueCod: "उच्च COD — ग्राहक की पहचान सत्यापित करें",
    codeVerified: "✓ कोड सत्यापित",
    sendCodeToCustomer: "ग्राहक को कोड भेजें",
    otpPlaceholder: "6 अंकों का कोड",
    verify: "सत्यापित",
    savedOnPhone: "✓ फ़ोन में सेव (अपलोड होगा)",
    doorPhotoReady: "✓ दरवाज़े की फ़ोटो तैयार",
    retake: "दोबारा",
    camera: "कैमरा",
    gallery: "गैलरी",
    cakeAtDoor: "दरवाज़े पर केक",
    slideToDeliver: "डिलीवर करने के लिए स्लाइड करें",
    orderComplete: "ऑर्डर पूर्ण",
    callCustomer: "ग्राहक को कॉल",

    cakeProof: "केक प्रूफ़",
    finishedCake: "तैयार केक",
    onScale: "तराज़ू पर",
    tapToEnlarge: "· बड़ा करने के लिए टैप",
    orderedWeight: "ऑर्डर वज़न:",
    openProofForCustomer: "ग्राहक के लिए प्रूफ़ पेज खोलें",

    toDropOff: "ड्रॉप-ऑफ़ तक",
    min: "मि",
    minutesToDropOff: "ड्रॉप-ऑफ़ तक {n} मिनट",
    syncingLiveRoute: "लाइव रूट सिंक हो रहा है",
    yourRoute: "आपका रास्ता",
    liveEtaRoads: "सड़क पर लाइव ETA",
    waitingGpsSync: "GPS सिंक का इंतज़ार…",
    tapDirections: "टर्न-बाय-टर्न दिशा के लिए नीचे टैप करें",
    liveRouteCustomer: "ग्राहक तक लाइव रास्ता",
    closeFullscreenMap: "पूर्ण स्क्रीन मैप बंद",
    routeMap: "रूट मैप",
    exitFullscreenMap: "पूर्ण स्क्रीन से बाहर",
    openFullscreenMap: "पूर्ण स्क्रीन मैप",
    openRouteGoogleMaps: "Google Maps में रास्ता खोलें",

    thankYou: "धन्यवाद, {name} जी!",
    orderNowComplete: "ऑर्डर अब पूर्ण है",
    backToAllDeliveries: "सभी डिलीवरी पर वापस",
    there: "आप",

    payLaterConfirm:
      "ग्राहक बाद में भुगतान करेगा?\n\nभुगतान प्रूफ़ के बिना ऑर्डर पूर्ण हो सकता है। स्लाइड करने पर ग्राहक को UPI भुगतान WhatsApp मिलेगा।",
    codPaidConfirm:
      "ऑर्डर को भुगतान किया हुआ मार्क करें?\n\nग्राहक ने ₹{amount} {mode} से भुगतान किया हो, तभी OK टैप करें। यह डिलीवरी पार्टनर को भुगतान नहीं करता।",
    otpSentAlert: "कोड ग्राहक को WhatsApp पर भेजा गया। उनसे पढ़कर सुनाएँ।",

    noInternetCache: "इंटरनेट नहीं — अच्छे सिग्नल पर एक बार लिंक खोलें, फिर कोशिश करें।",
    weakSignalCache: "कमज़ोर सिग्नल — सेव ऑर्डर दिख रहा है। सिग्नल बेहतर होने पर रीफ़्रेश करें।",
    photoSavedPhone: "अपलोड सेवा विफल रही। फ़ोटो इस फ़ोन में सुरक्षित है और अपने-आप फिर कोशिश होगी।",
    orderNotLoaded: "ऑर्डर लोड नहीं हुआ।",
    tapPaymentFirst: "ग्राहक से बात के बाद नकद, UPI, या बकाया (बाद में) टैप करें।",
    paymentNotConfirmed: "भुगतान अभी पुष्टि नहीं।",
    takeDoorPhoto: "पूर्ण करने से पहले दरवाज़े पर केक की फ़ोटो लें।",
    verifyOtpFirst: "पूर्ण करने से पहले ग्राहक OTP सत्यापित करें।",
    deliverySavedPhone: "डिलीवरी फ़ोन में सेव — इंटरनेट आने पर पूरी होगी।",
    couldNotUploadPhotos:
      "अपलोड सेवा ने सेव फ़ोटो स्वीकार नहीं की। फ़ोटो इस फ़ोन में सुरक्षित हैं; थोड़ा रुककर एक बार स्लाइड करें।",
    deliverySavedAuto: "डिलीवरी सेव — अपने आप सिंक होगी।",
    codPaidFailed: "भुगतान सेव नहीं हुआ। ऑर्डर रीफ़्रेश किया — स्थिति जाँचें और फिर कोशिश करें।",

    couldNotOpenPage: "ड्राइवर पेज नहीं खुल सका।",

    mapPinningDropOff: "मैप पर ड्रॉप-ऑफ़ लग रहा है…",
    mapAllowLocation: "लोकेशन अनुमति दें — GPS चालू होने पर ETA",
    mapBufferingLocation: "बफ़र — आपकी लोकेशन सिंक…",
    mapBufferingGps: "बफ़र — GPS रीफ़्रेश…",
    mapBufferingRoute: "बफ़र — ग्राहक तक रास्ता मिलान…",

    brandSubtitle: "The Table Top",
    docTitle: "डिलीवरी — The Table Top",
    priorityTitle: "⚡ प्राथमिकता ऑर्डर",
    priorityHint: "ग्राहक को तुरंत चाहिए — दूसरे ऑर्डर से पहले पूरा करें",
    photoLabel: "फ़ोटो",
    kgUnit: "किग्रा",

    slotMorning: "सुबह",
    slotNoon: "दोपहर",
    slotEvening: "शाम",
    slotNight: "रात",
    slotCustom: "कस्टम",

    errInvalidDriverLink: "गलत ड्राइवर लिंक",
    errSelectOrder: "डिलीवरी सूची से ऑर्डर चुनें",
    errNotOutYet: "यह ऑर्डर अभी डिलीवरी के लिए बाहर नहीं भेजा गया",
    errCouldNotVerify: "ऑर्डर सत्यापित नहीं हो सका",
    errCouldNotLoadOrder: "ऑर्डर लोड नहीं हुआ। फिर कोशिश करें या मैनेजर से पूछें।",
    errCouldNotLoadOrderRefresh: "ऑर्डर लोड नहीं हुआ। मैनेजर से ड्राइवर लिंक रीफ़्रेश करवाएँ।",
    errMarkPaymentFirstServer: "पूर्ण करने से पहले ड्राइवर लिंक पर नकद, UPI, या बकाया (बाद में) चिह्नित करें।",
    errOtpRequiredServer:
      "उच्च COD के लिए ग्राहक OTP ज़रूरी। ड्राइवर लिंक से कोड भेजें और सत्यापित करें।",
    errOrderNotFound: "ऑर्डर नहीं मिला",
    errDbFailed: "डेटाबेस कनेक्शन विफल",
    errInvalidHandoff: "ड्राइवर हैंडऑफ़ के लिए गलत ऑर्डर",
    errProofUploadFailed: "भुगतान प्रूफ़ अपलोड विफल",
    errDeliveryPhotoUploadFailed: "डिलीवरी फ़ोटो अपलोड विफल",
    errPhotosStillUploading: "सेव फ़ोटो अभी अपलोड हो रही हैं। थोड़ी देर बाद कोशिश करें।",
    errUploadFailed: "अपलोड विफल",

    installTitle: "डिलीवरी ऐप",
    installSubtitle: "ड्राइवर डैशबोर्ड",
    installContinue: "डिलीवरी खोलें",
    installAddHomeTitle: "होम स्क्रीन पर जोड़ें (दूसरे ऐप से अलग):",
    installStep1: "1. इस पेज पर रहें",
    installStep2: "2. Chrome मेनू (⋮) → Install app या Add to Home screen",
    installStep3Name: "3. नाम रखें Delivery",
    installAppName: "Delivery",
  },
};

/** Map liveTrackingMap buffer strings to translation keys */
const MAP_BUFFER_KEYS = {
  "Pinning drop-off on map…": "mapPinningDropOff",
  "Allow location — ETA when GPS is on": "mapAllowLocation",
  "Buffering — syncing your location…": "mapBufferingLocation",
  "Buffering — refreshing GPS…": "mapBufferingGps",
  "Buffering — matching route to customer…": "mapBufferingRoute",
};

export function translateDriverString(lang, key, params = {}) {
  const table = DRIVER_STRINGS[lang] || DRIVER_STRINGS.en;
  let text = table[key] ?? DRIVER_STRINGS.en[key] ?? key;
  Object.entries(params).forEach(([k, v]) => {
    text = text.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
  });
  return text;
}

export function translateMapBuffer(lang, message) {
  if (!message) return message;
  const key = MAP_BUFFER_KEYS[message];
  return key ? translateDriverString(lang, key) : message;
}

export function readStoredDriverLang() {
  try {
    const v = localStorage.getItem(DRIVER_LANG_STORAGE_KEY);
    return v === "hi" ? "hi" : "en";
  } catch {
    return "en";
  }
}

export function payLabelKey(label) {
  const u = String(label || "").trim().toUpperCase();
  if (u === "PAID") return "payPaid";
  if (u === "UNPAID") return "payUnpaid";
  return "payCod";
}

const ERROR_EXACT_KEYS = {
  "Invalid driver link": "errInvalidDriverLink",
  "Request failed": "requestFailed",
  "Select an order from the delivery list": "errSelectOrder",
  "This order is not out for delivery yet": "errNotOutYet",
  "Could not verify order": "errCouldNotVerify",
  "Could not load this order. Please try again or ask your manager.": "errCouldNotLoadOrder",
  "Could not load this order. Please ask your manager to refresh the driver link.":
    "errCouldNotLoadOrderRefresh",
  "Mark Cash, UPI, or Unpaid (pay later) on the driver link before completing.":
    "errMarkPaymentFirstServer",
  "High-value COD delivery requires customer OTP verification. Use the driver link to send and verify the code.":
    "errOtpRequiredServer",
  "Order not found": "errOrderNotFound",
  "Database connection failed": "errDbFailed",
  "Invalid order for driver handoff": "errInvalidHandoff",
  "Proof upload failed": "errProofUploadFailed",
  "Delivery photo upload failed": "errDeliveryPhotoUploadFailed",
  "Saved photos still uploading. Try again in a moment.": "errPhotosStillUploading",
  "Upload failed": "errUploadFailed",
  "Order not loaded.": "orderNotLoaded",
  "Tap Cash, UPI, or Unpaid (pay later) after talking to the customer.": "tapPaymentFirst",
  "Payment not confirmed yet.": "paymentNotConfirmed",
  "Take a photo of the cake at the door before completing.": "takeDoorPhoto",
  "Verify customer OTP before completing.": "verifyOtpFirst",
};

const SLOT_WINDOW_KEYS = {
  MORNING: "slotMorning",
  NOON: "slotNoon",
  EVENING: "slotEvening",
  NIGHT: "slotNight",
  CUSTOM: "slotCustom",
};


function extractErrorText(message) {
  const raw = String(message || "").trim();
  if (!raw) return "";
  if (raw.startsWith("{")) {
    try {
      const j = JSON.parse(raw);
      if (typeof j?.detail === "string") return j.detail.trim();
    } catch {
      /* ignore */
    }
  }
  return raw;
}

export function translateDriverError(lang, message) {
  const raw = extractErrorText(message);
  if (!raw || lang !== "hi") return raw || message;
  const exact = ERROR_EXACT_KEYS[raw];
  if (exact) return translateDriverString(lang, exact);
  return raw;
}

export function formatDriverDeliveryDate(lang, iso) {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(iso).trim())) {
    try {
      const dt = new Date(`${iso}T12:00:00`);
      return dt.toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  }
  return iso;
}

export function formatDriverDeliverySlot(lang, slot, customTime) {
  const parts = [];
  // TT-862 Phase 6C: the window name and the value shown beside it both come
  // from deliverySlotLabels, so a delivery written with a legacy spelling of
  // the night window still reads as NIGHT on the driver's run sheet.
  const window = deliverySlotWindowLabel(slot);
  const canonical = canonicalDeliverySlot(slot);
  if (window) {
    const label =
      lang === "hi"
        ? translateDriverString("hi", SLOT_WINDOW_KEYS[window])
        : window;
    parts.push(label);
    if (!isCustomDeliverySlot(canonical)) parts.push(canonical);
  } else if (slot) {
    parts.push(slot);
  }
  if (customTime) parts.push(customTime);
  return parts.join(" · ") || "—";
}

export function formatDriverWeight(lang, weightKg) {
  if (weightKg == null || weightKg === "") return "";
  const unit = translateDriverString(lang, "kgUnit");
  return `${weightKg} ${unit}`;
}

export function applyDriverDocumentTitle(lang) {
  if (typeof document === "undefined") return;
  document.title = translateDriverString(lang, "docTitle");
  const appleTitle = translateDriverString(lang, "titleDelivery");
  let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "apple-mobile-web-app-title";
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", appleTitle);
}
